import { useState } from 'react';
import {
  useSuppliers,
  useCreateSupplier,
  useUpdateSupplier,
  useDeleteSupplier,
  useBulkUpdateSupplierPrices,
  useSupplierProducts,
} from '@/hooks/useSuppliers';
import { useCategories } from '@/hooks/useProducts';
import { Supplier } from '@/services/suppliers';
import { useAuthStore } from '@/context/authStore';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import Modal from '@/components/ui/Modal';
import {
  Plus,
  Search,
  Edit,
  Trash2,
  Truck,
  Phone,
  Mail,
  MapPin,
  Package,
  TrendingUp,
  AlertCircle,
} from 'lucide-react';
import { formatPrice } from '@/utils/format';

export default function Suppliers() {
  const [search, setSearch] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState<Supplier | null>(null);
  const [deleteModal, setDeleteModal] = useState<Supplier | null>(null);
  const [priceUpdateModal, setPriceUpdateModal] = useState<Supplier | null>(null);

  const { user } = useAuthStore();
  const canWrite = user?.effective_permissions?.includes('purchases') ?? false;

  const { data: suppliers, isLoading } = useSuppliers({ search: search || undefined });
  const deleteSupplier = useDeleteSupplier();

  const handleDeleteSupplier = async () => {
    if (!deleteModal) return;
    try {
      await deleteSupplier.mutateAsync(deleteModal.id);
      setDeleteModal(null);
    } catch (error) {
      console.error('Error deleting supplier:', error);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Proveedores</h1>
          <p className="text-gray-600">Gestiona tus proveedores y actualiza precios masivamente</p>
        </div>
        {canWrite && (
          <Button onClick={() => setIsModalOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Nuevo Proveedor
          </Button>
        )}
      </div>

      {/* Filtros */}
      <div className="card">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Buscar proveedores..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Lista de proveedores */}
      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="text-center py-8">Cargando...</div>
        ) : suppliers && suppliers.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Proveedor
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Contacto
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Productos
                  </th>
                  {canWrite && (
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                      Acciones
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {suppliers.map((supplier) => (
                  <tr key={supplier.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 bg-blue-100 rounded-lg flex items-center justify-center">
                          <Truck className="h-5 w-5 text-blue-600" />
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {supplier.name}
                          </div>
                          {supplier.address && (
                            <div className="text-sm text-gray-500 flex items-center">
                              <MapPin className="h-3 w-3 mr-1" />
                              {supplier.address.length > 30
                                ? supplier.address.substring(0, 30) + '...'
                                : supplier.address}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        {supplier.contact_name && (
                          <div>{supplier.contact_name}</div>
                        )}
                        {supplier.phone && (
                          <div className="text-gray-500 flex items-center">
                            <Phone className="h-3 w-3 mr-1" />
                            {supplier.phone}
                          </div>
                        )}
                        {supplier.email && (
                          <div className="text-gray-500 flex items-center">
                            <Mail className="h-3 w-3 mr-1" />
                            {supplier.email}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        <Package className="h-3 w-3 mr-1" />
                        {supplier.products_count || 0} productos
                      </span>
                    </td>
                    {canWrite && (
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setPriceUpdateModal(supplier)}
                            title="Actualizar precios"
                            className="text-green-600 hover:text-green-700 hover:bg-green-50"
                            disabled={!supplier.products_count}
                          >
                            <TrendingUp className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setEditingSupplier(supplier);
                              setIsModalOpen(true);
                            }}
                            title="Editar"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDeleteModal(supplier)}
                            title="Eliminar"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            {search ? 'No se encontraron proveedores' : 'No hay proveedores registrados'}
          </div>
        )}
      </div>

      {/* Modal de proveedor */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingSupplier(null);
        }}
        title={editingSupplier ? 'Editar Proveedor' : 'Nuevo Proveedor'}
        size="md"
      >
        <SupplierForm
          supplier={editingSupplier}
          onSuccess={() => {
            setIsModalOpen(false);
            setEditingSupplier(null);
          }}
        />
      </Modal>

      {/* Modal de confirmación de eliminación */}
      <Modal
        isOpen={!!deleteModal}
        onClose={() => setDeleteModal(null)}
        title="Eliminar Proveedor"
        size="sm"
      >
        {deleteModal && (
          <div className="space-y-4">
            <p className="text-gray-600">
              ¿Estás seguro de que deseas eliminar <strong>{deleteModal.name}</strong>?
            </p>
            {deleteModal.products_count && deleteModal.products_count > 0 && (
              <div className="flex items-center gap-2 bg-yellow-50 text-yellow-800 p-3 rounded-lg text-sm">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                <span>
                  Este proveedor tiene {deleteModal.products_count} productos asociados.
                  Los productos no serán eliminados.
                </span>
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setDeleteModal(null)}>
                Cancelar
              </Button>
              <Button
                variant="danger"
                onClick={handleDeleteSupplier}
                loading={deleteSupplier.isPending}
              >
                Eliminar
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Modal de actualización masiva de precios */}
      <Modal
        isOpen={!!priceUpdateModal}
        onClose={() => setPriceUpdateModal(null)}
        title={`Actualizar Precios - ${priceUpdateModal?.name || ''}`}
        size="lg"
      >
        {priceUpdateModal && (
          <BulkPriceUpdateModal
            supplier={priceUpdateModal}
            onClose={() => setPriceUpdateModal(null)}
          />
        )}
      </Modal>
    </div>
  );
}

// Formulario de proveedor
function SupplierForm({
  supplier,
  onSuccess,
}: {
  supplier: Supplier | null;
  onSuccess: () => void;
}) {
  const [formData, setFormData] = useState({
    name: supplier?.name || '',
    contact_name: supplier?.contact_name || '',
    phone: supplier?.phone || '',
    email: supplier?.email || '',
    address: supplier?.address || '',
    notes: supplier?.notes || '',
  });

  const createSupplier = useCreateSupplier();
  const updateSupplier = useUpdateSupplier();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (supplier) {
        await updateSupplier.mutateAsync({ id: supplier.id, data: formData });
      } else {
        await createSupplier.mutateAsync(formData);
      }
      onSuccess();
    } catch (error) {
      console.error('Error saving supplier:', error);
    }
  };

  const isLoading = createSupplier.isPending || updateSupplier.isPending;

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <Input
        label="Nombre del proveedor *"
        value={formData.name}
        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
        required
        placeholder="Ej: Distribuidora Veterinaria"
      />

      <Input
        label="Nombre de contacto"
        value={formData.contact_name}
        onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
        placeholder="Ej: Juan Pérez"
      />

      <div className="grid grid-cols-2 gap-4">
        <Input
          label="Teléfono"
          value={formData.phone}
          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
          placeholder="Ej: 11-1234-5678"
        />
        <Input
          type="email"
          label="Email"
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          placeholder="Ej: proveedor@email.com"
        />
      </div>

      <Input
        label="Dirección"
        value={formData.address}
        onChange={(e) => setFormData({ ...formData, address: e.target.value })}
        placeholder="Ej: Av. Corrientes 1234, CABA"
      />

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Notas</label>
        <textarea
          value={formData.notes}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          rows={3}
          className="input"
          placeholder="Notas adicionales sobre el proveedor..."
        />
      </div>

      <div className="flex justify-end gap-2 pt-4">
        <Button type="submit" loading={isLoading}>
          {supplier ? 'Guardar Cambios' : 'Crear Proveedor'}
        </Button>
      </div>
    </form>
  );
}

// Modal de actualización masiva de precios
function BulkPriceUpdateModal({
  supplier,
  onClose,
}: {
  supplier: Supplier;
  onClose: () => void;
}) {
  const [percentage, setPercentage] = useState('');
  const [updateType, setUpdateType] = useState<'cost' | 'sale_price' | 'list_price'>('cost');
  const [recalculateFromCost, setRecalculateFromCost] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [reason, setReason] = useState('');
  const [showPreview, setShowPreview] = useState(false);

  const { data: products, isLoading: loadingProducts } = useSupplierProducts(supplier.id);
  const { data: categories } = useCategories();
  const bulkUpdate = useBulkUpdateSupplierPrices();

  const filteredProducts = products?.filter((p) => {
    if (!categoryFilter) return true;
    return p.category_id?.toString() === categoryFilter;
  });

  const handleSubmit = async () => {
    if (!percentage) return;

    try {
      await bulkUpdate.mutateAsync({
        supplierId: supplier.id,
        data: {
          category_id: categoryFilter ? parseInt(categoryFilter) : undefined,
          update_type: updateType,
          percentage: parseFloat(percentage),
          recalculate_from_cost: recalculateFromCost,
          reason: reason || `Aumento ${percentage}% - ${supplier.name}`,
        },
      });
      onClose();
    } catch (error) {
      console.error('Error updating prices:', error);
    }
  };

  const categoryOptions = [
    { value: '', label: 'Todas las categorías' },
    ...(categories?.map((c) => ({ value: c.id.toString(), label: c.name })) || []),
  ];

  const updateTypeOptions = [
    { value: 'cost', label: 'Aumentar costo y recalcular precios' },
    { value: 'sale_price', label: 'Solo precio efectivo (sin tocar costo)' },
    { value: 'list_price', label: 'Solo precio tarjeta (sin tocar costo)' },
  ];

  // Calcular preview
  const previewCalc = (cost: number) => {
    if (!percentage) return { newCost: cost, newSale: 0, newList: 0 };
    const mult = 1 + parseFloat(percentage) / 100;
    const newCost = cost * mult;
    // Usar fórmula simple: x2 y redondear
    const newSale = Math.ceil((newCost * 2) / 100) * 100;
    const newList = Math.round(newSale * 1.1);
    return { newCost, newSale, newList };
  };

  return (
    <div className="space-y-4">
      {/* Info del proveedor */}
      <div className="bg-blue-50 p-4 rounded-lg">
        <div className="flex items-center gap-3">
          <Truck className="h-8 w-8 text-blue-600" />
          <div>
            <h3 className="font-semibold text-blue-900">{supplier.name}</h3>
            <p className="text-sm text-blue-700">
              {supplier.products_count || 0} productos asociados
            </p>
          </div>
        </div>
      </div>

      {/* Configuración */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Input
            type="number"
            label="Porcentaje de aumento"
            value={percentage}
            onChange={(e) => setPercentage(e.target.value)}
            placeholder="Ej: 10"
            step="0.1"
          />
          <p className="text-xs text-gray-500 mt-1">
            Usa valores negativos para reducir (ej: -5)
          </p>
        </div>
        <Select
          label="Tipo de actualización"
          options={updateTypeOptions}
          value={updateType}
          onChange={(e) => setUpdateType(e.target.value as typeof updateType)}
        />
      </div>

      <Select
        label="Filtrar por categoría"
        options={categoryOptions}
        value={categoryFilter}
        onChange={(e) => setCategoryFilter(e.target.value)}
      />

      {updateType === 'cost' && (
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={recalculateFromCost}
            onChange={(e) => setRecalculateFromCost(e.target.checked)}
            className="rounded border-gray-300 text-primary-600"
          />
          <span className="text-sm">
            Recalcular precios de venta usando la fórmula de cada categoría
          </span>
        </label>
      )}

      <Input
        label="Motivo (opcional)"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="Ej: Aumento de lista de precios Enero 2026"
      />

      {/* Preview */}
      <div className="border-t pt-4">
        <button
          type="button"
          onClick={() => setShowPreview(!showPreview)}
          className="text-sm text-blue-600 hover:text-blue-800"
        >
          {showPreview ? 'Ocultar vista previa' : 'Ver vista previa de cambios'}
        </button>

        {showPreview && (
          <div className="mt-4 max-h-64 overflow-y-auto">
            {loadingProducts ? (
              <div className="text-center py-4">Cargando productos...</div>
            ) : filteredProducts && filteredProducts.length > 0 ? (
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">
                      Producto
                    </th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">
                      Costo actual
                    </th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">
                      Nuevo costo
                    </th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">
                      Nuevo efectivo
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredProducts.slice(0, 10).map((product) => {
                    const preview = previewCalc(product.cost_price || 0);
                    return (
                      <tr key={product.id}>
                        <td className="px-3 py-2">{product.name}</td>
                        <td className="px-3 py-2 text-right">
                          {formatPrice(product.cost_price)}
                        </td>
                        <td className="px-3 py-2 text-right text-green-600">
                          {formatPrice(preview.newCost)}
                        </td>
                        <td className="px-3 py-2 text-right text-blue-600">
                          {formatPrice(preview.newSale)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <p className="text-gray-500 text-center py-4">
                No hay productos para mostrar
              </p>
            )}
            {filteredProducts && filteredProducts.length > 10 && (
              <p className="text-sm text-gray-500 text-center mt-2">
                Y {filteredProducts.length - 10} productos más...
              </p>
            )}
          </div>
        )}
      </div>

      {/* Resumen */}
      {percentage && filteredProducts && (
        <div className="bg-yellow-50 p-4 rounded-lg">
          <div className="flex items-start gap-2">
            <AlertCircle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-yellow-800">
              <p className="font-medium">Resumen de la actualización:</p>
              <ul className="mt-1 list-disc list-inside">
                <li>
                  Se actualizarán <strong>{filteredProducts.length}</strong> productos
                </li>
                <li>
                  Incremento de <strong>{percentage}%</strong> en{' '}
                  {updateType === 'cost' ? 'costo' : updateType === 'sale_price' ? 'precio efectivo' : 'precio tarjeta'}
                </li>
                {recalculateFromCost && updateType === 'cost' && (
                  <li>Los precios de venta serán recalculados automáticamente según la fórmula de cada categoría</li>
                )}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Acciones */}
      <div className="flex justify-end gap-2 pt-4 border-t">
        <Button variant="secondary" onClick={onClose}>
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          loading={bulkUpdate.isPending}
          disabled={!percentage || !filteredProducts?.length}
        >
          Aplicar Actualización
        </Button>
      </div>
    </div>
  );
}
